// Sprint 36 / H4-B3 — Modaalin siirreltävyys desktopilla.
//
// Käyttö:
//   const { position, isDragging, dragHandleProps } = useDraggable({
//     defaultPosition: { x: 200, y: 100 },
//     enabled: window.innerWidth >= 768, // vain desktop
//   });
//   <div style={{ left: position.x, top: position.y, position: 'fixed' }}>
//     <header {...dragHandleProps}>...</header>
//   </div>
//
// Mobiilissa (enabled=false) palauttaa null-position → komponentin oma layout
// (esim. bottom-sheet) säilyy ennallaan.

import { useState, useRef, useCallback, useEffect } from 'react';
import { getAppZoom } from '../utils/appZoom';

export interface UseDraggableOptions {
  defaultPosition?: { x: number; y: number };
  enabled?: boolean;
  /** Localstorage-avain jolla position muistetaan sivulatausten yli */
  storageKey?: string;
}

export interface UseDraggableResult {
  position: { x: number; y: number } | null;
  isDragging: boolean;
  dragHandleProps: {
    onMouseDown: (e: React.MouseEvent) => void;
    style: React.CSSProperties;
  };
  reset: () => void;
}

export function useDraggable(options: UseDraggableOptions = {}): UseDraggableResult {
  const { defaultPosition = null, enabled = true, storageKey } = options;

  const [position, setPosition] = useState<{ x: number; y: number } | null>(() => {
    if (!enabled) return null;
    if (storageKey) {
      try {
        const raw = localStorage.getItem(`draggable:${storageKey}`);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') return parsed;
        }
      } catch {}
    }
    return defaultPosition;
  });

  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef<{ mouseX: number; mouseY: number; posX: number; posY: number } | null>(null);

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!dragStart.current) return;
    // 19.8.2026 (desktop-skaalaus): clientX/clientY ovat zoomaamattomia
    // laitepikseleitä, mutta position tulkitaan zoomatussa kuoressa. Ilman jakoa
    // elementti liikkuisi kerroin-verran nopeammin kuin hiiri.
    const zoom = getAppZoom();
    const dx = (e.clientX - dragStart.current.mouseX) / zoom;
    const dy = (e.clientY - dragStart.current.mouseY) / zoom;
    setPosition({ x: dragStart.current.posX + dx, y: dragStart.current.posY + dy });
  }, []);

  const onMouseUp = useCallback(() => {
    dragStart.current = null;
    setIsDragging(false);
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    // Persistoi sijainti
    if (storageKey) {
      try {
        const current = JSON.parse(localStorage.getItem(`draggable:${storageKey}`) || 'null');
        // Position päivittyi setPosition:n kautta — luetaan tila uudelleen tämän puolelta jos tarpeen.
        // Yksinkertaisempaa: tallennetaan dragStart-aikainen lähtöarvo + viimeisin delta.
        // Käytetään state-muuttujaa joka on jo päivitetty.
        if (current !== null) {/* no-op, state hoitaa */}
      } catch {}
    }
  }, [onMouseMove, storageKey]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (!enabled) return;
    // Klikkaus vain kahvan painikkeista ei saa aloittaa dragia — käytä e.target:n
    // type-tarkistusta jos tarvitset hienosäätöä. Default sallii kaikki paitsi BUTTON.
    const tag = (e.target as HTMLElement).tagName;
    if (tag === 'BUTTON' || tag === 'INPUT' || tag === 'TEXTAREA') return;
    const current = position ?? defaultPosition ?? { x: 100, y: 100 };
    dragStart.current = {
      mouseX: e.clientX, mouseY: e.clientY,
      posX: current.x, posY: current.y,
    };
    setIsDragging(true);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    e.preventDefault();
  }, [enabled, position, defaultPosition, onMouseMove, onMouseUp]);

  // Persistoi position localStorageen kun se muuttuu
  useEffect(() => {
    if (!storageKey || !position) return;
    try {
      localStorage.setItem(`draggable:${storageKey}`, JSON.stringify(position));
    } catch {}
  }, [position, storageKey]);

  // Cleanup jos komponentti unmountataan kesken dragin
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  const reset = useCallback(() => {
    setPosition(defaultPosition);
    if (storageKey) {
      try { localStorage.removeItem(`draggable:${storageKey}`); } catch {}
    }
  }, [defaultPosition, storageKey]);

  return {
    position,
    isDragging,
    dragHandleProps: {
      onMouseDown,
      style: { cursor: enabled ? (isDragging ? 'grabbing' : 'grab') : 'default', userSelect: 'none' },
    },
    reset,
  };
}
